import { useCallback, useMemo } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

// Old ids pile up as ideas close, so only the newest are kept.
export const MAX_READ_IDS = 500

export function addReadId(ids: readonly string[], id: string): string[] {
	if (ids.includes(id)) return ids as string[]
	return [...ids, id].slice(-MAX_READ_IDS)
}

interface ReadIdeasState {
	byUser: Record<string, string[]>
	markRead: (userId: string, id: string) => void
}

// One store, so the sidebar's dot clears the moment the page opens an idea.
// Kept per browser for now; a read-state table can replace it without touching callers.
export const useReadIdeasStore = create<ReadIdeasState>()(
	persist(
		(set) => ({
			byUser: {},
			markRead: (userId, id) =>
				set((state) => {
					const current = state.byUser[userId] ?? []
					const next = addReadId(current, id)
					if (next === current) return state
					return { byUser: { ...state.byUser, [userId]: next } }
				}),
		}),
		{ name: "agent-ideas-read" },
	),
)

export function useReadIdeas(userId: string) {
	const ids = useReadIdeasStore((s) => s.byUser[userId])
	const mark = useReadIdeasStore((s) => s.markRead)
	const read = useMemo(() => new Set(ids ?? []), [ids])
	const isUnread = useCallback((id: string) => !read.has(id), [read])
	const markRead = useCallback((id: string) => mark(userId, id), [mark, userId])
	return { isUnread, markRead }
}
